import React, { useCallback, useState } from 'react';
import { useResizingHeight } from '@atlaskit/motion';
import {
	SpotlightManager,
	SpotlightPulse,
	SpotlightTarget,
	SpotlightTransition,
} from '@atlaskit/onboarding';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { Section } from '@atlaskit/side-navigation';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { useProject } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import { STARRED_QUEUE_SPOTLIGHT } from '@atlassian/jira-servicedesk-common/src/constants.tsx';
import { CoordinationClientWithFallback } from '@atlassian/jira-servicedesk-itsm-onboarding-common/src/ui/coordination-client-with-fallback/index.tsx';
import { QueueListSection } from '@atlassian/jira-servicedesk-queues-categorized-store/src/controllers/navigation-category/index.tsx';
import { useFavouriteNavItems } from '@atlassian/jira-servicedesk-queues-categorized-store/src/main.tsx';
import type { ItsmPractice } from '@atlassian/jira-servicedesk-work-category/src/common/constants.tsx';
import { QueueList } from '../queue-list/main.tsx';
import { messages } from './messages.tsx';
import StarredOnboardingSpotlight from './onboarding/index.tsx';

type StarredSectionProps = {
	practice: ItsmPractice;
};

export const StarredSection = ({ practice }: StarredSectionProps) => {
	const { data: project } = useProject();
	const { formatMessage } = useIntl();
	const projectKey = project ? project.key : '';
	const [queues] = useFavouriteNavItems('', projectKey, practice);
	const [isSpotlightActive, setIsSpotlightActive] = useState(false);
	const [showPulse, setShowPulse] = useState(true);
	const hasNoStarredQueues = queues?.every((queue) => !queue.starred);
	const onClose = () => setIsSpotlightActive(false);

	const emptyStarredOnClick = useCallback(() => {
		if (showPulse) {
			setShowPulse(false);
			setIsSpotlightActive(true);
		}
	}, [showPulse]);

	const emptyStarredContainer = (
		<Inline as="span" xcss={emptyStarredContainerStyles}>
			<FormattedMessage {...messages.emptyStarredSectionMessage} />
		</Inline>
	);

	const starredSection = (
		<div {...useResizingHeight()}>
			<Section title={formatMessage(messages.starredHeader)}>
				{queues && queues.length !== 0 ? (
					<QueueList queues={queues} section={QueueListSection.STARRED} />
				) : (
					emptyStarredContainer
				)}
			</Section>
		</div>
	);

	let spotlightPulseContainer;
	if (showPulse) {
		spotlightPulseContainer = (
			<Box
				testId="navigation-apps-sidebar-servicedesk.common.ui.practice-queues.starred-section.starred-queues"
				as="span"
				role="button"
				onClick={emptyStarredOnClick}
				xcss={pulseContainerWithPulseStyles}
				onKeyDown={(e: React.KeyboardEvent) => {
					e.key === 'Enter' && emptyStarredOnClick();
				}}
				tabIndex={0}
			>
				{starredSection}
			</Box>
		);
	} else {
		spotlightPulseContainer = <span>{starredSection}</span>;
	}

	return hasNoStarredQueues ? (
		<CoordinationClientWithFallback
			messageId={STARRED_QUEUE_SPOTLIGHT}
			messageType="engagement"
			fallback={starredSection}
		>
			<SpotlightManager>
				<SpotlightTarget name="starred-queues">
					<SpotlightPulse pulse={showPulse} radius={3}>
						{spotlightPulseContainer}
					</SpotlightPulse>
				</SpotlightTarget>
				<SpotlightTransition>
					{isSpotlightActive && <StarredOnboardingSpotlight onClose={onClose} />}
				</SpotlightTransition>
			</SpotlightManager>
		</CoordinationClientWithFallback>
	) : (
		starredSection
	);
};

const emptyStarredContainerStyles = xcss({
	color: 'color.text.subtlest',
	font: 'font.body.small',
	paddingInlineStart: 'space.100',
});

const pulseContainerWithPulseStyles = xcss({
	cursor: 'pointer',
});
